import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import type { AnyAction } from '@reduxjs/toolkit';
import {
  AlertBanner,
  Box,
  Button,
  ButtonIcon,
  Chip,
  Icon,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import { useInvertedLocalize, useTypedSelector } from '../../shared/hooks';
import { createDevRejectionHandler } from '../../shared/utils';

import type { AuditLogViewerBodyHandle } from './AuditLogViewerBody/AuditLogViewerBody';
import type { CompleteReviewData } from './CompleteReviewModal/types';
import { useLazyGetAuditSummaryQuery } from './data-layer/auditLogApi';
import type { ReviewActivity } from './data-layer/auditLogTypes';
import { isFfRoleRequestType } from './data-layer/auditLogUtils';
import {
  preloadSessionRecordingAssets,
  useRecordingSelection,
  useRecordingSessionsViewModel,
} from './hooks/useRecordingSessions';
import {
  getRecordingPlaybackNotFoundLaunchIds,
  subscribeRecordingPlaybackNotFound,
} from './SessionRecording/playbackNotFound';
import SessionRecording, {
  prefetchRecordingPlayback,
} from './SessionRecording/SessionRecording';
import type { TimelineData } from './TimeLineBlock/types';
import { dispatchActivityLogMutationFailureBanner } from './utils/dispatchActivityLogMutationFailureBanner';
import { handleRecordingTimestampSelection } from './utils/recordingSelection';
import { formatDateByGlobal, navigateBackToSource } from './utils/utils';
import { AuditLogDetailPanel } from './AuditLogDetailPanel';
import { InsightsPanel } from './AuditLogLeftSection';
import { AuditLogViewerBody } from './AuditLogViewerBody';
import { AuditLogViewerHeader } from './AuditLogViewerHeader';
import AuditLogViewerRecordingFrame from './AuditLogViewerRecordingFrame';
import AuditLogViewerVideoPlaylist from './AuditLogViewerVideoPlaylist';
import {
  useCompleteReviewMutation,
  useLazyGetAuditLogReviewsQuery,
} from './data-layer';
import { auditLogActions } from './data-store';
import { useApiCancellation, useDateFormat } from './hooks';

import './AuditLogViewer.css';

// Phase 1: Lazy load CompleteReviewModal - only loaded when isCompleteReviewModalOpen is true
const CompleteReviewModal = React.lazy(() =>
  import('./CompleteReviewModal/index.js').then(
    (module: {
      CompleteReviewModal: React.ComponentType<{
        isOpen: boolean;
        onClose: () => void;
        onComplete: (data: CompleteReviewData) => void;
        activitySummaryCounts?: {
          flagged: number;
          commented: number;
          resolved: number;
        };
        summaryError?: string;
        isSubmitting: boolean;
      }>;
    }) => ({
      default: module.CompleteReviewModal,
    })
  )
);

/**
 * AuditLogViewer Component
 *
 * Main component for the audit log viewer page.
 * Contains header and body sections.
 */
function AuditLogViewer() {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();
  const [isInsightsOpen, setIsInsightsOpen] = useState(true);
  const bodyRef = useRef<AuditLogViewerBodyHandle | null>(null);
  const [selectedTimelineItem, setSelectedTimelineItem] =
    useState<TimelineData | null>(null);
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false);
  const [isRecordingOpen, setIsRecordingOpen] = useState(false);
  const [openTimelineItemId, setOpenTimelineItemId] = useState<
    string | undefined
  >(undefined);
  const [isCompleteReviewModalOpen, setIsCompleteReviewModalOpen] =
    useState(false);
  const [completeReviewSuccess, setCompleteReviewSuccess] = useState<
    string | null
  >(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [summaryCounts, setSummaryCounts] = useState<{
    flagged: number;
    commented: number;
    resolved: number;
  } | null>(null);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [detailPanelDefaultTab, setDetailPanelDefaultTab] = useState<
    'summary' | 'review-activity'
  >('summary');

  const [noRecordingBanner, setNoRecordingBanner] = useState<{
    key: number;
    message: string;
  } | null>(null);

  // Playlist carousel is handled in AuditLogViewerVideoPlaylist

  // Redux-driven data and error
  const timelineData = useTypedSelector((s) => s.aag.auditLog.timelineData) as
    | TimelineData[]
    | undefined;

  // Keeping originalTimelineData reference removed as it is unused after modal change

  const safeTimelineData = useMemo(
    () => (Array.isArray(timelineData) ? timelineData : []),
    [timelineData]
  );

  // Count flags that need resolution
  const flagCount = useMemo(
    () =>
      safeTimelineData.filter(
        (item: TimelineData) => item.rowLogStatus === 'FLAGGED'
      ).length,
    [safeTimelineData]
  );

  const handleInsightsToggle = () => {
    setIsInsightsOpen(!isInsightsOpen);

    // Close detail panel if insights is being opened
    if (isDetailPanelOpen) {
      setIsDetailPanelOpen(false);
      setSelectedTimelineItem(null);
    }
  };

  const handleInsightsClose = () => {
    setIsInsightsOpen(false);
  };

  const [completeReview] = useCompleteReviewMutation();
  const [triggerGetReviews] = useLazyGetAuditLogReviewsQuery();
  const [triggerGetSummary] = useLazyGetAuditSummaryQuery();
  const { registerAbort, cancelRequest } = useApiCancellation();

  const requestMetadata = useTypedSelector(
    (s) => s.aag.auditLog.requestMetadata
  );
  const isFfRoleRequest = isFfRoleRequestType(requestMetadata?.requestType);

  const handleTimelineItemClickRef = useRef<
    (item: TimelineData, tab?: 'summary' | 'review-activity') => void
  >(() => {});

  handleTimelineItemClickRef.current = (
    item: TimelineData,
    tab?: 'summary' | 'review-activity'
  ) => {
    const openDetailPanel = () => {
      setSelectedTimelineItem(item);
      setOpenTimelineItemId(item.uniqueLogId);
      setDetailPanelDefaultTab(tab ?? 'summary');
      setIsDetailPanelOpen(true);

      // Always close insights panel when detail panel is opened
      setIsInsightsOpen(false);
    };

    if (isRecordingOpen) {
      setIsRecordingOpen(false);
      setIsRecordingPlayerView(false);
      setMp4SeekSeconds(null);
      setOpenTimelineItemId(undefined);
    }

    openDetailPanel();

    // Fetch latest activity on click so Summary and Review Activity tabs are up to date
    if (requestMetadata?.requestAccessKey) {
      // Cancel previous reviews request if any
      cancelRequest('getAuditLogReviews');

      const promise = triggerGetReviews({
        uniqueLogId: item.uniqueLogId,
        requestAccessKey: requestMetadata.requestAccessKey,
      });

      // Register abort function for this request
      const abortablePromise = registerAbort(
        promise as unknown as ReturnType<typeof triggerGetReviews> & {
          abort?: () => void;
        },
        'getAuditLogReviews'
      );

      abortablePromise
        .unwrap()
        .then((res) => {
          const activities = Array.isArray(
            (res as { reviewActivities?: unknown }).reviewActivities
          )
            ? ((res as { reviewActivities?: unknown })
                .reviewActivities as unknown[])
            : [];

          dispatch(
            auditLogActions.setReviewActivities({
              uniqueLogId: item.uniqueLogId,
              activities: activities as never[],
            })
          );

          const setLatestActivityAction =
            auditLogActions.setTimelineItemLatestActivity as unknown as (payload: {
              uniqueLogId: string | number;
              activities: ReviewActivity[];
            }) => AnyAction;

          dispatch(
            setLatestActivityAction({
              uniqueLogId: item.uniqueLogId,
              activities: activities as ReviewActivity[],
            })
          );

          // Also update the locally selected item so the detail panel sees latestActivity immediately
          if (activities.length > 0) {
            const a = activities[0] as {
              status?: string;
              user?: string | null;
              comment?: string | null;
              updatedAt?: string | number | Date;
            };

            setSelectedTimelineItem((curr) =>
              curr && curr.uniqueLogId === item.uniqueLogId
                ? {
                    ...curr,
                    latestActivity: {
                      status: String(a.status ?? 'COMMENTED'),
                      updatedBy: a.user ?? null,
                      comment: a.comment ?? null,
                      updatedAt: formatDateByGlobal(
                        a.updatedAt ?? '',
                        globalDateFormat
                      ),
                    },
                  }
                : curr
            );
          }
        })
        .catch(() => {
          // ignore
        });
    }
  };

  const handleTimelineItemClick = useCallback(
    (item: TimelineData, tab?: 'summary' | 'review-activity') => {
      handleTimelineItemClickRef.current(item, tab);
    },
    []
  );

  // Snapshot to detect list reorder while an action is in-flight
  const listSignatureRef = useRef<string>('');

  const onDetailPrepareAdvance = (activeTab: 'summary' | 'review-activity') => {
    // capture current list signature from body
    try {
      const sig = bodyRef.current?.getListSignature();

      listSignatureRef.current = String(sig ?? '');
      // persist the active tab to maintain after advance
      setDetailPanelDefaultTab(activeTab);
    } catch (_) {
      // ignore
    }
  };

  const isReadOnly = useTypedSelector((s) => s.aag.auditLog.isReadOnly);
  const isAllLogsLoading = useTypedSelector((s) => s.aag.auditLog.isLoading);
  const globalDateFormat = useDateFormat();
  const launchDetail = useTypedSelector((s) => s.aag.auditLog.launchDetail);
  const [excludedRecordingLaunchIds, setExcludedRecordingLaunchIds] = useState<
    Set<string>
  >(() => new Set());

  useEffect(() => {
    if (isFfRoleRequest) {
      return () => {};
    }

    const unsubscribe = subscribeRecordingPlaybackNotFound(
      (launchId: string) => {
        setExcludedRecordingLaunchIds((prev) => {
          if (prev.has(launchId)) {
            return prev;
          }

          const next = new Set(prev);

          next.add(launchId);

          return next;
        });
      }
    );

    return unsubscribe;
  }, [isFfRoleRequest]);

  // Early asset preload and playback prefetch as soon as we know there are recordings
  useEffect(() => {
    if (isFfRoleRequest) {
      return;
    }

    if (Array.isArray(launchDetail) && launchDetail.length > 0) {
      preloadSessionRecordingAssets().catch(() => {
        // ignore preload errors; will retry on demand
      });

      const prefetchPromises: Promise<void>[] = launchDetail
        .filter((session) => session.launchId && session.launchEndDate)
        .map((session) => prefetchRecordingPlayback(session.launchId));

      if (prefetchPromises.length > 0) {
        Promise.allSettled(prefetchPromises)
          .then(() => {
            setExcludedRecordingLaunchIds(
              new Set(getRecordingPlaybackNotFoundLaunchIds())
            );
          })
          .catch(() => {
            // ignore prefetch errors
          });
      }
    }
  }, [launchDetail, isFfRoleRequest]);

  const recordingSessionsViewModel = useRecordingSessionsViewModel({
    launchDetail: isFfRoleRequest ? [] : launchDetail,
    requestMetadata,
    globalDateFormat,
    localize: l,
    excludedLaunchIds: excludedRecordingLaunchIds,
  });

  const {
    recordedSessions,
    recordingSelectedIndex,
    setRecordingSelectedIndex,
    isRecordingPlayerView,
    setIsRecordingPlayerView,
    hasRecordedSessions,
  } = useRecordingSelection(recordingSessionsViewModel);
  const [mp4SeekSeconds, setMp4SeekSeconds] = useState<number | null>(null);

  const handleDetailPanelClose = useCallback(() => {
    setIsDetailPanelOpen(false);
    setSelectedTimelineItem(null);
    setOpenTimelineItemId(undefined);
  }, [setIsDetailPanelOpen, setSelectedTimelineItem]);

  // Close detail panel once per loading cycle when /search is loading
  const prevLoadingRef = useRef<boolean>(false);

  useEffect(() => {
    const wasLoading = prevLoadingRef.current;

    if (!wasLoading && isAllLogsLoading && isDetailPanelOpen) {
      handleDetailPanelClose();
    }

    prevLoadingRef.current = isAllLogsLoading;
  }, [isAllLogsLoading, isDetailPanelOpen, handleDetailPanelClose]);

  // Also close detail panel when filter/search signature changes (parity with auditor view)
  const activeFiltersSig = useTypedSelector((s) =>
    JSON.stringify(s.aag.auditLog.activeFilters)
  );
  const searchQueryRaw = useTypedSelector((s) =>
    String(s.aag.auditLog.searchQuery)
  );
  const prevSigRef = useRef<string>('');

  useEffect(() => {
    const sig = `${activeFiltersSig}|${searchQueryRaw}`;
    const prev = prevSigRef.current;

    if (isDetailPanelOpen && prev && prev !== sig) {
      handleDetailPanelClose();
    }

    prevSigRef.current = sig;
  }, [
    activeFiltersSig,
    searchQueryRaw,
    isDetailPanelOpen,
    handleDetailPanelClose,
  ]);

  const onDetailAdvanceToNext = (activeTab: 'summary' | 'review-activity') => {
    // Do not auto-advance in auditor mode
    if (isReadOnly) {
      return;
    }

    // If list signature has changed (reordered due to server updates), close panel
    const currentSig = bodyRef.current?.getListSignature() ?? '';

    if (String(currentSig) !== String(listSignatureRef.current)) {
      handleDetailPanelClose();

      return;
    }

    const currentId = selectedTimelineItem?.uniqueLogId;

    if (currentId == null) {
      return;
    }

    const nextId = bodyRef.current?.getNextVisibleItemId(currentId) ?? null;

    if (nextId === null) {
      // No next item; keep panel open on current item
      return;
    }

    // Find the next item in current timeline data
    const arr = Array.isArray(timelineData) ? timelineData : [];
    const nextItem = arr.find((i) => String(i.uniqueLogId) === String(nextId));

    if (!nextItem) {
      return;
    }

    // Keep side panel open; maintain same tab; change content
    setSelectedTimelineItem(nextItem);
    setDetailPanelDefaultTab(activeTab);
    setIsDetailPanelOpen(true);

    // Smooth scroll into view
    bodyRef.current?.scrollItemIntoView(nextId);
  };

  const closeRecordingPanel = useCallback(() => {
    if (!isRecordingOpen) {
      return;
    }

    setIsRecordingOpen(false);
    setIsRecordingPlayerView(false);
    setMp4SeekSeconds(null);
    setOpenTimelineItemId(undefined);
  }, [
    isRecordingOpen,
    setIsRecordingOpen,
    setIsRecordingPlayerView,
    setMp4SeekSeconds,
  ]);

  useEffect(() => {
    if (isFfRoleRequest && isRecordingOpen) {
      closeRecordingPanel();
    }
  }, [isFfRoleRequest, isRecordingOpen, closeRecordingPanel]);

  const openRecordingPanel = useCallback(
    (nextSelectedIndex?: number) => {
      if (isFfRoleRequest) {
        return;
      }

      setIsRecordingOpen(true);

      // If the user opened recordings manually (not from a timestamp click),
      // clear any previous seek target so playback starts from the beginning.
      if (typeof nextSelectedIndex !== 'number') {
        setMp4SeekSeconds(null);
      }

      if (isInsightsOpen) {
        setIsInsightsOpen(false);
      }

      if (isDetailPanelOpen) {
        const logIdToKeep = selectedTimelineItem?.uniqueLogId;

        handleDetailPanelClose();

        // Preserve which timeline row to keep expanded so connector to Session Recording still shows
        if (logIdToKeep != null) {
          setOpenTimelineItemId(logIdToKeep);
        }
      }

      // Kick off preload/prefetch for the currently selected session when opening
      if (recordedSessions.length > 0) {
        const idxToUse =
          typeof nextSelectedIndex === 'number'
            ? nextSelectedIndex
            : recordingSelectedIndex;
        const currentId = recordedSessions[idxToUse]?.id;

        preloadSessionRecordingAssets().catch(() => {
          // ignore preload errors
        });

        if (currentId) {
          prefetchRecordingPlayback(currentId).catch(() => {
            // ignore prefetch errors; fallback will retry on player mount
          });
        }
      }
    },
    [
      isInsightsOpen,
      isDetailPanelOpen,
      selectedTimelineItem,
      recordedSessions,
      recordingSelectedIndex,
      setIsRecordingOpen,
      setIsInsightsOpen,
      setMp4SeekSeconds,
      setOpenTimelineItemId,
      isFfRoleRequest,
      handleDetailPanelClose,
    ]
  );

  const handleRecordingToggle = useCallback(() => {
    if (isRecordingOpen) {
      closeRecordingPanel();
    } else {
      openRecordingPanel();
    }
  }, [isRecordingOpen, closeRecordingPanel, openRecordingPanel]);

  const handleDetailPanelUtcTimestampClick = useCallback(() => {
    if (isFfRoleRequest) {
      return;
    }

    const nextSeek = handleRecordingTimestampSelection({
      timestampIso: selectedTimelineItem?.logCreatedAt,
      sessions: recordedSessions,
      localize: l,
      setNoRecordingBanner,
      setRecordingSelectedIndex,
      setIsRecordingPlayerView,
      openRecordingPanel,
    });

    setMp4SeekSeconds(nextSeek);
  }, [
    l,
    selectedTimelineItem,
    recordedSessions,
    setNoRecordingBanner,
    setRecordingSelectedIndex,
    setIsRecordingPlayerView,
    openRecordingPanel,
    setMp4SeekSeconds,
    isFfRoleRequest,
  ]);

  const handleTimelineTimestampClick = useCallback(
    (item: TimelineData) => {
      if (isFfRoleRequest) {
        return;
      }

      const nextSeek = handleRecordingTimestampSelection({
        timestampIso: item.logCreatedAt,
        sessions: recordedSessions,
        localize: l,
        setNoRecordingBanner,
        setRecordingSelectedIndex,
        setIsRecordingPlayerView,
        openRecordingPanel,
      });

      setMp4SeekSeconds(nextSeek);

      // Row expansion (connector) logic:
      // - No recording + detail panel closed: collapse row to avoid width expansion (connector
      //   extending into empty space causes the grey background to stretch).
      // - No recording + detail panel open: keep connector on selected row (don't touch).
      // - Has recording + detail panel open: openRecordingPanel handles openTimelineItemId.
      // - Has recording + detail panel closed: expand clicked row for recording panel.
      if (nextSeek === null && !isDetailPanelOpen) {
        setOpenTimelineItemId(undefined);
      } else if (nextSeek !== null && !isDetailPanelOpen) {
        setOpenTimelineItemId(item.uniqueLogId);
      }
    },
    [
      l,
      recordedSessions,
      isDetailPanelOpen,
      setNoRecordingBanner,
      setRecordingSelectedIndex,
      setIsRecordingPlayerView,
      openRecordingPanel,
      setMp4SeekSeconds,
      isFfRoleRequest,
    ]
  );

  const handleCompleteReviewClick = () => {
    setIsCompleteReviewModalOpen(true);
    setSummaryError(null);

    // Fetch summary on demand when opening modal
    const rak = requestMetadata?.requestAccessKey;

    if (rak != null && rak !== '') {
      triggerGetSummary({ requestAccessKey: rak })
        .unwrap()
        .then((data) => {
          setSummaryCounts({
            flagged: Number((data as { flagged?: unknown }).flagged ?? 0),
            commented: Number((data as { commented?: unknown }).commented ?? 0),
            resolved: Number((data as { resolved?: unknown }).resolved ?? 0),
          });
        })
        .catch(() => {
          setSummaryError(
            l('Activity summary unavailable. Please try again later')
          );
          setSummaryCounts(null);
        });
    } else {
      setSummaryError(
        l('Activity summary unavailable. Please try again later')
      );
      setSummaryCounts(null);
    }
  };

  const handleCompleteReviewClose = () => {
    setIsCompleteReviewModalOpen(false);
    setSummaryError(null);
    setCompleteReviewSuccess(null);
  };

  const handleSuccessBannerDismiss = () => {
    setCompleteReviewSuccess(null);
    navigateBackToSource();
  };

  const handleCompleteReviewSubmit = async (data: CompleteReviewData) => {
    if (!requestMetadata?.requestAccessKey) {
      return;
    }

    setIsSubmittingReview(true);
    setCompleteReviewSuccess(null);

    try {
      const result = completeReview({
        requestAccessKey: requestMetadata.requestAccessKey,
        body: {
          reviewComment: data.comment,
        },
      });

      await result.unwrap();

      setIsCompleteReviewModalOpen(false);
      const successMessage = l('Success');

      setCompleteReviewSuccess(successMessage);
    } catch (error) {
      let errorMessage = l('Failed to complete review. Please try again.');

      if (error && typeof error === 'object') {
        const rtqError = error as {
          data?:
            | {
                response?: {
                  data?: {
                    message?: string;
                  };
                };
                message?: string;
              }
            | string;
          message?: string;
        };

        if (
          rtqError.data &&
          typeof rtqError.data === 'object' &&
          rtqError.data.response?.data?.message
        ) {
          errorMessage = rtqError.data.response.data.message;
        } else if (
          rtqError.data &&
          typeof rtqError.data === 'object' &&
          rtqError.data.message
        ) {
          errorMessage = rtqError.data.message;
        } else if (typeof rtqError.data === 'string') {
          errorMessage = rtqError.data;
        } else if (rtqError.message) {
          errorMessage = rtqError.message;
        }
      }

      dispatchActivityLogMutationFailureBanner(dispatch, error, l, {
        genericFailureTitle: l('Failed to Complete Review'),
        genericFailureDescription: errorMessage,
      });
    } finally {
      setIsSubmittingReview(false);
    }
  };

  return (
    <div
      className={cx('Sod-AuditLogViewer', {
        'Sod-AuditLogViewer--withRecordingPanel': isRecordingOpen,
        'Sod-AuditLogViewer--withDetailPanel':
          isDetailPanelOpen && !isRecordingOpen,
        'Sod-AuditLogViewer--withInsightsPanel': isInsightsOpen,
      })}>
      <AlertBanner
        key={noRecordingBanner?.key ?? 'no-recording'}
        colorTheme='Information'
        title={l('No Session Recording Found')}
        description={noRecordingBanner?.message ?? ''}
        isVisible={Boolean(noRecordingBanner)}
        onCancel={() => setNoRecordingBanner(null)}
        isFloating
        shouldAutoDismiss
        autoDismissDelay={3000}
      />
      <AlertBanner
        colorTheme='Success'
        title={completeReviewSuccess ?? ''}
        description={l(
          'Review submitted. You can close this message, or wait to be redirected.'
        )}
        isVisible={Boolean(completeReviewSuccess)}
        onCancel={handleSuccessBannerDismiss}
        isFloating
        shouldAutoDismiss
        autoDismissDelay={5000}
      />
      <div className='Sod-AuditLogViewer-wrapper'>
        <AuditLogViewerHeader />
        <div className='Sod-AuditLogViewer-wrapper-body-detailPanel'>
          <AuditLogViewerBody
            ref={bodyRef}
            isInsightsOpen={isInsightsOpen}
            onInsightsToggle={handleInsightsToggle}
            onTimelineItemClick={handleTimelineItemClick}
            onTimelineTimestampClick={
              isFfRoleRequest ? undefined : handleTimelineTimestampClick
            }
            selectedTimelineItemId={
              openTimelineItemId ??
              selectedTimelineItem?.uniqueLogId ??
              undefined
            }
            isDetailPanelOpen={isDetailPanelOpen}
            isRecordingOpen={isRecordingOpen}
            onRecordingToggle={handleRecordingToggle}
            closeDetailPanel={handleDetailPanelClose}
          />

          {/* Detail Panel - Full height side panel */}
          {isDetailPanelOpen && selectedTimelineItem && (
            <AuditLogDetailPanel
              item={
                safeTimelineData.find(
                  (t) =>
                    String(t.uniqueLogId) ===
                    String(selectedTimelineItem.uniqueLogId)
                ) ?? selectedTimelineItem
              }
              onClose={handleDetailPanelClose}
              onPrepareAdvance={onDetailPrepareAdvance}
              onAdvanceToNextItem={onDetailAdvanceToNext}
              defaultActiveTab={detailPanelDefaultTab}
              onUtcLogStartTimeClick={
                !isFfRoleRequest && hasRecordedSessions
                  ? handleDetailPanelUtcTimestampClick
                  : undefined
              }
              onViewRecording={
                !isFfRoleRequest && hasRecordedSessions
                  ? handleDetailPanelUtcTimestampClick
                  : undefined
              }
            />
          )}
        </div>

        {/* Insights Panel - Full height side panel - Only visible when panel is open and not in readOnly mode */}
        {isInsightsOpen && !isReadOnly && (
          <Box className='Sod-AuditLogViewer-insightsPanel'>
            <InsightsPanel onClose={handleInsightsClose} />
          </Box>
        )}

        {/* Recording Panel - Covers half the page horizontally */}
        {isRecordingOpen && (
          <Box className='Sod-AuditLogViewer-recordingPanel'>
            <Box className='Sod-AuditLogViewer-recordingPanel-header'>
              <Typography kind='h2'>
                <span>{l('Session Recording')}</span>
              </Typography>
              <ButtonIcon
                size='medium'
                kind='ghost'
                ariaLabel={l('Close')}
                icon={<Icon kind='Close' size='medium' />}
                onClick={() => {
                  setIsRecordingPlayerView(false);
                  handleRecordingToggle();
                }}
              />
            </Box>
            <Box
              className={
                isRecordingPlayerView
                  ? 'Sod-AuditLogViewer-recordingPanel-content-playlist-view'
                  : 'Sod-AuditLogViewer-recordingPanel-content'
              }>
              {(() => {
                if (recordedSessions.length === 0) {
                  return (
                    <Box className='Sod-AuditLogViewer-recordingPanel-content-empty'>
                      <Typography
                        kind='body2'
                        color='--color-foreground-neutral-subtle'>
                        {l('No Session Recording Found')}
                      </Typography>
                    </Box>
                  );
                }

                const cardSessions = recordedSessions.map((session) => ({
                  id: session.id,
                  src: session.previewSrc,
                  title: session.title,
                  dateTime: session.dateTime,
                  duration: session.duration,
                }));

                const getSessionAt = (
                  idx: number
                ): (typeof recordedSessions)[number] | undefined => {
                  if (idx < 0 || idx >= recordedSessions.length) {
                    return undefined;
                  }

                  return recordedSessions[idx];
                };

                const selectedSession =
                  getSessionAt(recordingSelectedIndex) ?? getSessionAt(0);

                if (isRecordingPlayerView) {
                  // Defensive: in rare transient states (e.g., selection index becoming invalid
                  // due to sessions list changing), avoid dereferencing undefined.
                  if (!selectedSession) {
                    return (
                      <Box className='Sod-AuditLogViewer-recordingPanel-content-empty'>
                        <Typography
                          kind='body2'
                          color='--color-foreground-neutral-subtle'>
                          {l('No Session Recording Found')}
                        </Typography>
                      </Box>
                    );
                  }

                  return (
                    <>
                      <AuditLogViewerVideoPlaylist
                        sessions={recordedSessions.map((session) => ({
                          id: session.id,
                          title: session.title,
                          meta: `${session.dateTime} • ${session.duration}`,
                        }))}
                        selectedIndex={recordingSelectedIndex}
                        onSelect={(i: number) => {
                          setRecordingSelectedIndex(i);
                          setIsRecordingPlayerView(true);
                          setMp4SeekSeconds(null);
                        }}
                        ariaPrevLabel={l('Previous')}
                        ariaNextLabel={l('Next')}
                      />
                      <Box className='Sod-Recording-playlist-video-player'>
                        <SessionRecording
                          key={`rec-session-${selectedSession.id}`}
                          launchId={selectedSession.id}
                          launchStartDate={selectedSession.launchStartDate}
                          seekSeconds={mp4SeekSeconds ?? undefined}
                        />
                      </Box>
                    </>
                  );
                }

                return (
                  <>
                    {Array.from({
                      length: Math.ceil(cardSessions.length / 2),
                    }).map((_, frameIdx) => (
                      <AuditLogViewerRecordingFrame
                        key={`rec-frame-${frameIdx + 1}`}
                        baseIndex={frameIdx * 2}
                        sessions={cardSessions}
                        onSelect={(index: number) => {
                          setRecordingSelectedIndex(index);
                          setIsRecordingPlayerView(true);
                          setMp4SeekSeconds(null);
                        }}
                      />
                    ))}
                  </>
                );
              })()}
            </Box>
          </Box>
        )}

        {/* Footer - Fixed at bottom of page */}
        <Box className='Sod-AuditLogViewer-footer'>
          <Box className='Sod-AuditLogViewer-Footer-action-bar'>
            <Box className='Sod-AuditLogViewer-Footer-actionBar-button'>
              {flagCount > 0 && (
                <Chip
                  kind='boldFilled'
                  size='small'
                  color='warning'
                  leftIcon={<Icon kind='AlertWarningOutline' size='small' />}
                  label={`${flagCount} Flag${flagCount === 1 ? '' : 's'} Need${
                    flagCount === 1 ? 's' : ''
                  } Resolution`}
                  onClick={() => {
                    // NOTE: Flag chip click functionality will be implemented (could scroll to first flagged item)
                  }}
                />
              )}
              <Box className='' />
              <Box className=''>
                <Button
                  type='button'
                  kind='filled'
                  size='medium'
                  onClick={handleCompleteReviewClick}
                  isDisabled={isSubmittingReview}>
                  {l('Complete Review')}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Complete Review Modal */}
        {isCompleteReviewModalOpen && (
          <Suspense fallback={null}>
            <CompleteReviewModal
              isOpen={isCompleteReviewModalOpen}
              onClose={handleCompleteReviewClose}
              onComplete={(data: CompleteReviewData) => {
                handleCompleteReviewSubmit(data).catch(
                  createDevRejectionHandler(
                    'AuditLogViewer: handleCompleteReviewSubmit'
                  )
                );
              }}
              activitySummaryCounts={summaryCounts ?? undefined}
              summaryError={summaryError ?? undefined}
              isSubmitting={isSubmittingReview}
            />
          </Suspense>
        )}
      </div>
    </div>
  );
}

export default AuditLogViewer;
